import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import {
  Button,
  Drawer,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "components/snackbar/SnackbarContext";
import { SHOWN_USER_ROLE } from "consts/UserManagement";
import {
  editUserProfile,
  updateUserProfilePicture,
} from "features/account/service/AccountProfileService";
import { capitalize } from "lodash";
import { useEffect, useRef, useState } from "react";

const AccountEditSidebarComponent = ({
  user,
  isOpen,
  onBack,
  onClose,
  onSuccess,
}) => {
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("+62");
  const [position, setPosition] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [fullName, setFullName] = useState("");
  const fileInputRef = useRef(null);

  const { setMessage } = useSnackbar();
  const [profilePictureUrl, setProfilePictureUrl] = useState();
  const [previousData, setPreviousData] = useState(null);

  useEffect(() => {
    if (!user) return;
    setEmail(user.email);
    setPhoneNumber(user.phone_number.replace("+62", ""));
    setPosition(user.role);
    setFullName(user.full_name);
    setProfilePictureUrl(user.avatar_path);
    setPreviousData(user);
  }, [user]);

  const getChangedFields = (prevData, currData) => {
    return Object.fromEntries(
      Object.entries(currData).filter(
        ([key, value]) => prevData[key] !== value,
      ),
    );
  };

  const submit = async () => {
    setSubmitted(true);
    if (!email || !fullName || !phoneNumber) {
      setMessage({
        title: "Failed to edit",
        desc: "Please fill in all fields",
        type: "error",
      });
      return;
    }
    const currentData = {
      email: email,
      full_name: fullName,
      phone_number: "+62" + phoneNumber,
      role: position,
    };
    const sanitizedData = getChangedFields(previousData, currentData);
    const response = await editUserProfile(sanitizedData);
    if (response?.info?.title === "success") {
      setMessage({
        title: "Success",
        desc: "User updated",
        type: "success",
      });
      setSubmitted(false);
      onSuccess();
      onBack();
    } else {
      setMessage({
        title: "Failed",
        desc: response,
        type: "error",
      });
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setMessage({
          title: "Failed",
          desc: "File exceeds 2 MB",
          type: "error",
        });
        fileInputRef.current.value = "";
        return;
      }
      const form = new FormData();
      form.append("file", file);
      const response = await updateUserProfilePicture(form);
      if (response?.info?.title === "success") {
        const reader = new FileReader();
        reader.onload = (e) => {
          setProfilePictureUrl(e.target?.result);
        };
        reader.readAsDataURL(file);
        setMessage({
          title: "Success",
          desc: "Profile picture updated",
          type: "success",
        });
        onSuccess();
      } else {
        setMessage({
          title: "Failed",
          desc: response,
          type: "error",
        });
        fileInputRef.current.value = "";
        return;
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemovePhoto = async () => {
    setProfilePictureUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    const form = new FormData();
    form.append("file", null);
    const response = await updateUserProfilePicture(form);
    if (response?.info?.title === "success") {
      setMessage({
        title: "Success",
        desc: "Profile picture removed",
        type: "success",
      });
      onSuccess();
    } else {
      setMessage({
        title: "Failed",
        desc: response,
        type: "error",
      });
      fileInputRef.current.value = "";
      return;
    }
  };

  return (
    <Drawer className='account-profile-drawer' anchor='right' open={isOpen}>
      <Stack
        className='header'
        direction='row'
        justifyContent='space-between'
        alignItems='center'
      >
        <img
          alt='close-profile-icon'
          className='cursor-pointer'
          src='/images/sidebar/sidebar-back.svg'
          onClick={onBack}
        />
        <p className='account-profile-title'>Edit Profile</p>
        <img
          alt='close-profile-icon'
          className='cursor-pointer'
          src='/images/close-profile-icon.svg'
          onClick={onClose}
        />
      </Stack>
      <Stack className='container' spacing={3}>
        <Stack spacing={3} alignItems='center'>
          {profilePictureUrl ? (
            <img
              alt='account-profile'
              className='w-40 h-40 rounded-full shrink-0 object-cover'
              src={profilePictureUrl}
            />
          ) : (
            <AccountCircleRoundedIcon
              viewBox='2 2 20 20'
              aria-label='account-profile-image-default'
              className='p-0'
              sx={{ height: "160px", width: "160px", color: "#ccc" }}
            />
          )}
          <Typography color='#5F737F' textAlign='center' fontSize={12}>
            Max. file size 2MB (.jpg,
            <br /> .jpeg, .png)
          </Typography>
          <Stack spacing={1}>
            <input
              type='file'
              accept='.jpg,.jpeg,.png'
              onChange={handleFileChange}
              ref={fileInputRef}
              style={{ display: "none" }}
            />
            <Button
              variant='outlined'
              color='secondary'
              onClick={handleUploadClick}
              className='w-[200px]'
            >
              <Typography fontWeight={800} fontSize={12}>
                Upload Photo
              </Typography>
            </Button>
            <Button
              color='error'
              onClick={handleRemovePhoto}
              disabled={!profilePictureUrl}
              className='w-[200px]'
            >
              <Typography fontWeight={800} fontSize={12}>
                Remove Photo
              </Typography>
            </Button>
          </Stack>
        </Stack>
        <Stack spacing={1}>
          <Typography fontSize={12}>Full Name</Typography>
          <TextField
            error={submitted && !fullName}
            name='name'
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </Stack>
        <Stack spacing={1}>
          <Typography fontSize={12}>E-mail Address</Typography>
          <TextField
            error={submitted && !email}
            name='email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Stack>
        <Stack spacing={1}>
          <Typography fontSize={12}>Phone Number</Typography>
          <TextField
            error={submitted && !phoneNumber}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  sx={{
                    backgroundColor: "#EEF1F8",
                    padding: "20px 14px",
                    borderRight: "1px solid #727273",
                    borderTopLeftRadius: "10px",
                    borderBottomLeftRadius: "10px",
                  }}
                  position='start'
                >
                  <p className='account-edit-profile-form-input-label'>+62</p>
                </InputAdornment>
              ),
            }}
            name='phone'
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
        </Stack>
        <Stack spacing={1}>
          <Typography fontSize={12}>Role</Typography>
          <TextField
            disabled
            name='position'
            value={SHOWN_USER_ROLE?.[position] || capitalize(position)}
            onChange={(e) => setPosition(e.target.value)}
          />
        </Stack>
        <Stack spacing={2} direction='row' justifyContent='flex-end'>
          <Button color='secondary' onClick={onBack} variant='outlined'>
            <Typography fontSize={12}>Cancel</Typography>
          </Button>
          <Button
            onClick={submit}
            variant='contained'
            className='!bg-[#556FB5]'
          >
            <Typography fontSize={12}>Save Changes</Typography>
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default AccountEditSidebarComponent;
